/**
 * CaptureSheet — the Capture flow shell. Wires the full NL pipeline to the user:
 *   textarea → voice → parse → confirm card → user taps Save → apply.
 *
 * NEVER auto-saves. Every save requires an explicit Save button tap on the
 * ConfirmCard (UX-AC-021 / AC-080).
 *
 * The intent_id returned by /api/intents/parse is held in the Zustand store
 * until the user either saves or cancels — the audit row persists either way.
 */
import * as React from 'react';
import { Microphone, MicrophoneSlash } from '@phosphor-icons/react';
import type {
  CategoryId,
  ParsedIntent,
  PlaceId,
  PlaceStatus,
  TagId,
} from '@shared/types';
import { Sheet } from './ui/Sheet';
import { TextArea } from './ui/TextArea';
import { TextInput } from './ui/TextInput';
import { Button } from './ui/Button';
import { Skeleton } from './ui/Skeleton';
import { FormField } from './ui/FormField';
import { DotRating, type DotValue } from './ui/DotRating';
import { ConfirmCard } from './ConfirmCard';
import { useAppStore, useToast } from '../lib/store';
import { useActiveHouseholdId } from '../lib/household';
import { useOnline } from '../lib/online';
import {
  useApplyIntent,
  useCategories,
  useCreatePlace,
  useCreateVisit,
  useParseIntent,
  usePlaces,
  useTags,
} from '../lib/queries';
import { useVoice } from '../lib/voice';
import { useErrorToasts } from '../lib/errorToast';
import { cn } from '../lib/cn';

const LS_VOICE_NOTICE = 'cn:voiceNoticeDismissed';
const STATUSES: PlaceStatus[] = ['untried', 'go-to', 'neutral', 'avoid'];

type CaptureMode = 'speak' | 'forms';

export function CaptureSheet() {
  const open = useAppStore((s) => s.capture.sheetOpen);
  const closeSheet = useAppStore((s) => s.closeCaptureSheet);
  const draftText = useAppStore((s) => s.capture.draftText);
  const setDraftText = useAppStore((s) => s.setCaptureDraftText);
  const pendingIntentId = useAppStore((s) => s.capture.pendingIntentId);
  const setPendingIntentId = useAppStore((s) => s.setPendingIntentId);
  const resetCapture = useAppStore((s) => s.resetCapture);
  const householdId = useActiveHouseholdId();
  const toast = useToast();
  const online = useOnline();

  const voice = useVoice();
  const parse = useParseIntent();
  const categoriesQ = useCategories(householdId);
  const tagsQ = useTags(householdId);
  // Household-wide place candidates for the very-low-confidence picker
  // (AC-082). Limit to a reasonable surface — the picker shows the top
  // recent matches only.
  const placesQ = usePlaces(householdId, { limit: 24, sort: 'recent_visit' });
  const apply = useApplyIntent(householdId, pendingIntentId ?? '');

  const [mode, setMode] = React.useState<CaptureMode>('speak');

  const [voiceNoticeDismissed, setVoiceNoticeDismissed] = React.useState(() => {
    try {
      return window.localStorage.getItem(LS_VOICE_NOTICE) === '1';
    } catch {
      return false;
    }
  });

  // Append voice transcript to the textarea draft on finalize.
  const lastSeenTranscriptRef = React.useRef('');

  // Defensive: whenever the sheet transitions to closed, ensure no draft state
  // bleeds into the next session. The Save and Cancel paths already call
  // resetCapture(), but this catches the Forms-tab save path and any backdrop
  // dismissal while in the confirm-card state.
  React.useEffect(() => {
    if (!open) {
      resetCapture();
      lastSeenTranscriptRef.current = '';
    }
  }, [open, resetCapture]);
  React.useEffect(() => {
    if (
      voice.transcript &&
      voice.transcript !== lastSeenTranscriptRef.current
    ) {
      const additions = voice.transcript.slice(
        lastSeenTranscriptRef.current.length,
      );
      lastSeenTranscriptRef.current = voice.transcript;
      if (additions.trim()) {
        const next = draftText
          ? `${draftText.trimEnd()} ${additions.trim()}`
          : additions.trim();
        setDraftText(next);
      }
    }
  }, [voice.transcript, draftText, setDraftText]);

  // Cmd/Ctrl+K to open is wired at the AppShell level.

  function dismissVoiceNotice() {
    try {
      window.localStorage.setItem(LS_VOICE_NOTICE, '1');
    } catch {
      /* silent */
    }
    setVoiceNoticeDismissed(true);
  }

  async function onParse() {
    if (!draftText.trim()) return;
    try {
      const result = await parse.mutateAsync({ rawText: draftText });
      setPendingIntentId(result.intent_id);
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Couldn't reach the parser. Try again.",
      );
    }
  }

  async function onSave(params: {
    confirmed: ParsedIntent;
    place_id?: PlaceId | 'new';
    new_place?: { name: string; category_id: CategoryId };
  }) {
    try {
      await apply.mutateAsync(params);
      toast.success('Entry saved.');
      resetCapture();
      lastSeenTranscriptRef.current = '';
      voice.reset();
      setMode('speak');
      closeSheet();
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "We couldn't save that entry.",
      );
    }
  }

  function onCancel() {
    if (voice.listening) voice.stop();
    if (parse.data || pendingIntentId) {
      // Keep the audit row; just discard the in-memory pending id.
      setPendingIntentId(null);
      parse.reset();
      return;
    }
    if (draftText.length > 30) {
      // UX §4.8 — soft "discard" prompt is a toast acknowledging the cancel.
      toast.info('Draft discarded.');
    }
    resetCapture();
    voice.reset();
    lastSeenTranscriptRef.current = '';
    closeSheet();
  }

  const parsedResult = parse.data;

  return (
    <Sheet
      open={open}
      onOpenChange={(v) => (v ? null : onCancel())}
      title="Capture an entry"
      description="Tell us where you went, by voice or written down."
      side="bottom"
    >
      <div className="mx-auto max-w-[720px]">
        <header className="mb-4">
          <p className="font-sans text-xs uppercase tracking-[0.14em] text-ink-500">
            Capture
          </p>
          <h2 className="mt-1 font-serif text-2xl text-ink-900 leading-tight">
            Tell us where you went.
          </h2>
        </header>

        {/* Tab control — Speak (default) / Write it out (forms fallback) */}
        <div
          role="tablist"
          aria-label="Capture mode"
          className="mb-6 flex items-center gap-6 border-b border-ink-100"
        >
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'speak'}
            aria-controls="capture-tab-speak"
            onClick={() => setMode('speak')}
            className={cn(
              'pb-3 -mb-px font-sans text-xs uppercase tracking-[0.14em]',
              'transition-colors duration-200 ease-out-slow',
              'focus-visible:outline focus-visible:outline-1 focus-visible:outline-accent',
              mode === 'speak'
                ? 'text-ink-900 border-b-2 border-accent'
                : 'text-ink-500 hover:text-ink-900',
            )}
          >
            Speak it
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'forms'}
            aria-controls="capture-tab-forms"
            onClick={() => setMode('forms')}
            className={cn(
              'pb-3 -mb-px font-sans text-xs uppercase tracking-[0.14em]',
              'transition-colors duration-200 ease-out-slow',
              'focus-visible:outline focus-visible:outline-1 focus-visible:outline-accent',
              mode === 'forms'
                ? 'text-ink-900 border-b-2 border-accent'
                : 'text-ink-500 hover:text-ink-900',
            )}
          >
            Write it out
          </button>
        </div>

        {!online ? (
          <div
            role="status"
            className="mb-6 px-4 py-3 border border-ink-200 bg-surface-2 font-serif italic text-sm text-ink-700 leading-snug"
            data-testid="capture-offline-banner"
          >
            You&apos;re offline. We&apos;ll let you write when you reconnect.
          </div>
        ) : null}

        {mode === 'speak' ? (
        <div id="capture-tab-speak" role="tabpanel">
        <div className="relative">
          <TextArea
            value={
              voice.listening && voice.interim
                ? `${draftText}${draftText ? ' ' : ''}${voice.interim}`
                : draftText
            }
            onChange={(e) => setDraftText(e.target.value)}
            placeholder="Last night, dinner at Augusta's. The lamb was incredible…"
            minRows={4}
            disabled={parse.isPending || apply.isPending}
            autoFocus
          />
          {voice.supported ? (
            <div className="absolute right-0 top-1.5 flex items-center gap-2">
              {voice.listening ? (
                <span
                  aria-live="polite"
                  className="text-[0.65rem] uppercase tracking-widest text-accent italic"
                >
                  Listening…
                </span>
              ) : null}
              <button
                type="button"
                onClick={() => (voice.listening ? voice.stop() : voice.start())}
                aria-pressed={voice.listening}
                aria-label={
                  voice.error === 'permission-denied'
                    ? 'Microphone permission denied'
                    : voice.listening
                      ? 'Stop voice dictation'
                      : 'Start voice dictation'
                }
                title={
                  voice.error === 'permission-denied'
                    ? 'Microphone permission denied'
                    : undefined
                }
                className={cn(
                  'p-2 transition-colors duration-200 ease-out-slow',
                  voice.listening
                    ? 'text-accent'
                    : voice.error === 'permission-denied'
                      ? 'text-ink-400'
                      : 'text-ink-600 hover:text-ink-900',
                  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-accent',
                )}
              >
                {voice.error === 'permission-denied' ? (
                  <MicrophoneSlash size={20} weight="regular" />
                ) : voice.listening ? (
                  <span className="relative inline-flex">
                    <span
                      aria-hidden
                      className="h-4 w-4 rounded-full bg-accent animate-voice-pulse"
                    />
                  </span>
                ) : (
                  <Microphone size={20} weight="regular" />
                )}
              </button>
            </div>
          ) : null}
        </div>

        {voice.supported ? (
          <p className="mt-3 font-serif italic text-xs text-ink-500 leading-relaxed">
            Voice can mis-hear menu items and names — give it a quick scan
            before continuing.
          </p>
        ) : null}

        {!voice.supported && !voiceNoticeDismissed ? (
          <div className="mt-3 flex items-baseline justify-between gap-3 text-xs text-ink-500">
            <span>Voice not supported in this browser.</span>
            <button
              type="button"
              onClick={dismissVoiceNotice}
              className="text-[0.65rem] uppercase tracking-widest text-ink-500 hover:text-ink-900 focus-visible:outline focus-visible:outline-1 focus-visible:outline-accent"
            >
              Dismiss
            </button>
          </div>
        ) : null}

        {voice.error && voice.error !== 'not-supported' ? (
          <p
            role="status"
            aria-live="polite"
            className="mt-3 text-xs text-ink-500"
          >
            {voice.error === 'no-speech'
              ? "We didn't catch anything — check your microphone and try again."
              : voice.error === 'permission-denied'
                ? 'Microphone permission is off. Enable it in your browser to dictate.'
                : voice.error === 'service-unavailable'
                  ? "Couldn't reach the voice service. Try again in a moment."
                  : "Voice didn't start. Try again."}
          </p>
        ) : null}

        <div className="mt-6 flex items-center justify-end gap-3">
          <Button variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={onParse}
            loading={parse.isPending}
            loadingLabel="Reading…"
            disabled={!draftText.trim() || parse.isPending || !online}
          >
            Continue
          </Button>
        </div>

        {parse.isPending ? (
          <section className="mt-6 bg-surface border border-ink-200 p-6 md:p-8">
            <Skeleton className="h-6 w-2/3 mb-4" />
            <div className="grid gap-3 sm:grid-cols-2">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
              <Skeleton className="h-4 w-2/3" />
              <Skeleton className="h-4 w-1/2" />
            </div>
            <Skeleton className="h-16 w-full mt-6" />
          </section>
        ) : null}

        {parsedResult && pendingIntentId ? (
          <ConfirmCard
            parsed={parsedResult.parsed}
            confidence={parsedResult.confidence}
            {...(parsedResult.place_match
              ? {
                  matchedPlaceName: parsedResult.place_match.name,
                  matchedPlaceId: parsedResult.place_match.place_id,
                }
              : {})}
            onSave={onSave}
            onCancel={() => {
              setPendingIntentId(null);
              parse.reset();
            }}
            loading={apply.isPending}
            categories={categoriesQ.data?.categories.map((c) => ({
              id: c.id,
              name: c.name,
            })) ?? []}
            candidates={(placesQ.data?.places ?? []).map((p) => {
              const cat = categoriesQ.data?.categories.find(
                (c) => c.id === p.category_id,
              );
              return cat
                ? { id: p.id, name: p.name, hint: cat.name }
                : { id: p.id, name: p.name };
            })}
          />
        ) : null}
        </div>
        ) : (
          <div id="capture-tab-forms" role="tabpanel">
            <FormsCaptureTab
              householdId={householdId}
              categories={categoriesQ.data?.categories ?? []}
              tags={tagsQ.data?.tags ?? []}
              online={online}
              onSuccess={() => {
                toast.success('Entry saved.');
                setMode('speak');
                closeSheet();
              }}
              onCancel={onCancel}
            />
          </div>
        )}
      </div>
    </Sheet>
  );
}

// ---------- Forms-mode capture tab (AC-094 / AC-095 / AC-096) ----------

interface FormsCaptureTabProps {
  householdId: ReturnType<typeof useActiveHouseholdId>;
  categories: Array<{ id: CategoryId; name: string }>;
  tags: Array<{ id: TagId; name: string }>;
  online: boolean;
  onSuccess: () => void;
  onCancel: () => void;
}

function FormsCaptureTab({
  householdId,
  categories,
  tags,
  online,
  onSuccess,
  onCancel,
}: FormsCaptureTabProps) {
  const createPlace = useCreatePlace(householdId);
  const createVisit = useCreateVisit(householdId);
  const errors = useErrorToasts();
  const toast = useToast();

  const [placeName, setPlaceName] = React.useState('');
  const [address, setAddress] = React.useState('');
  const [categoryId, setCategoryId] = React.useState<CategoryId | ''>('');
  const [status, setStatus] = React.useState<PlaceStatus>('untried');
  const [visitedAt, setVisitedAt] = React.useState(() =>
    new Date().toISOString().slice(0, 10),
  );
  const [rating, setRating] = React.useState<DotValue>(0);
  const [notes, setNotes] = React.useState('');
  const [peopleText, setPeopleText] = React.useState('');
  const [tagIds, setTagIds] = React.useState<TagId[]>([]);
  const [touched, setTouched] = React.useState(false);

  React.useEffect(() => {
    if (categories.length && !categoryId) {
      setCategoryId(categories[0]!.id);
    }
  }, [categories, categoryId]);

  const nameError =
    touched && !placeName.trim() ? 'Give the place a name.' : '';
  const categoryError =
    touched && !categoryId ? 'Choose a category.' : '';
  const ratingError =
    touched && rating === 0 ? 'Pick a rating from 1 to 5 dots.' : '';
  const formInvalid =
    Boolean(nameError) || Boolean(categoryError) || Boolean(ratingError);

  const busy = createPlace.isPending || createVisit.isPending;

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setTouched(true);
    if (!placeName.trim() || !categoryId || rating === 0) return;
    try {
      // Two-step: create place, then create visit. We use `useCreatePlace`
      // and `useCreateVisit` so the cache invalidation contracts are
      // identical to the rest of the app (no inline apiFetch).
      const place = await createPlace.mutateAsync({
        name: placeName.trim(),
        category_id: categoryId,
        ...(address.trim() ? { address: address.trim() } : {}),
        status,
        ...(tagIds.length ? { tag_ids: tagIds } : {}),
      });
      await createVisit.mutateAsync({
        place_id: place.id,
        visited_at: `${visitedAt}T12:00:00Z`,
        rating: rating as 1 | 2 | 3 | 4 | 5,
        ...(notes.trim() ? { notes: notes.trim() } : {}),
        ...(tagIds.length ? { tag_ids: tagIds } : {}),
        // People are surfaced as a free-text hint. The backend's
        // /api/places/:id/people endpoint is separate; v1 surfaces the
        // names inside notes so they aren't lost while keeping the form
        // simple (the AC permits "optional people" as a free-text field).
        ...(peopleText.trim()
          ? {
              structured_json: {
                people_hint: peopleText
                  .split(',')
                  .map((p) => p.trim())
                  .filter(Boolean),
              },
            }
          : {}),
      });
      onSuccess();
    } catch (err) {
      if (!errors.handle(err)) {
        toast.error(
          err instanceof Error
            ? err.message
            : "We couldn't save that entry.",
        );
      }
    }
  }

  return (
    <form onSubmit={onSubmit} noValidate className="space-y-6">
      {formInvalid && touched ? (
        <div
          className="px-4 py-3 border border-error-muted/40 bg-error-soft"
          role="alert"
        >
          <p className="font-serif italic text-sm text-error-muted leading-snug">
            A few details need attention before we can save this entry.
          </p>
        </div>
      ) : null}

      <FormField
        label="Place"
        htmlFor="forms-place-name"
        {...(nameError ? { error: nameError } : {})}
      >
        <TextInput
          id="forms-place-name"
          value={placeName}
          onChange={(e) => setPlaceName(e.target.value)}
          placeholder="Augusta's"
          required
        />
      </FormField>

      <FormField label="Address" optional htmlFor="forms-place-address">
        <TextInput
          id="forms-place-address"
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          placeholder="10 Pine Ave"
        />
      </FormField>

      <FormField
        label="Category"
        htmlFor="forms-place-category"
        {...(categoryError ? { error: categoryError } : {})}
      >
        <select
          id="forms-place-category"
          value={categoryId}
          onChange={(e) => setCategoryId(e.target.value as CategoryId)}
          className={cn(
            'block w-full bg-transparent border-0 border-b border-ink-300 px-0 py-3',
            'font-sans text-base text-ink-900',
            'focus:border-b-2 focus:border-accent focus:outline-none',
          )}
          required
        >
          <option value="">Pick a category</option>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </FormField>

      <FormField label="Status" htmlFor="forms-place-status">
        <select
          id="forms-place-status"
          value={status}
          onChange={(e) => setStatus(e.target.value as PlaceStatus)}
          className={cn(
            'block w-full bg-transparent border-0 border-b border-ink-300 px-0 py-3',
            'font-sans text-base text-ink-900',
            'focus:border-b-2 focus:border-accent focus:outline-none',
          )}
        >
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </FormField>

      <FormField label="Date" htmlFor="forms-visited-at">
        <TextInput
          id="forms-visited-at"
          type="date"
          value={visitedAt}
          onChange={(e) => setVisitedAt(e.target.value)}
          required
        />
      </FormField>

      <FormField
        label="Rating"
        {...(ratingError ? { error: ratingError } : {})}
      >
        <DotRating value={rating} onChange={(v) => setRating(v)} size="md" />
      </FormField>

      <FormField label="Notes" optional htmlFor="forms-notes">
        <TextArea
          id="forms-notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="What stood out?"
          minRows={3}
        />
      </FormField>

      <FormField label="People" optional htmlFor="forms-people">
        <TextInput
          id="forms-people"
          value={peopleText}
          onChange={(e) => setPeopleText(e.target.value)}
          placeholder="Sam, Lisa"
        />
      </FormField>

      {tags.length ? (
        <FormField label="Tags" optional>
          <div className="flex flex-wrap gap-2">
            {tags.map((t) => {
              const active = tagIds.includes(t.id);
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() =>
                    setTagIds((prev) =>
                      active ? prev.filter((id) => id !== t.id) : [...prev, t.id],
                    )
                  }
                  aria-pressed={active}
                  className={cn(
                    'px-3 py-1 border text-xs lowercase rounded-full',
                    'transition-colors duration-200 ease-out-slow',
                    'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-accent',
                    active
                      ? 'bg-accent-soft border-accent text-accent'
                      : 'bg-transparent border-ink-200 text-ink-700 hover:bg-surface-2',
                  )}
                >
                  {t.name}
                </button>
              );
            })}
          </div>
        </FormField>
      ) : null}

      <div className="flex items-center justify-end gap-3 pt-2">
        <Button type="button" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="primary"
          loading={busy}
          loadingLabel="Saving…"
          disabled={busy || !online}
        >
          Save entry
        </Button>
      </div>
    </form>
  );
}
